"use client"

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { ADMIN_API_CONFIG } from "@/constants/api-configs"
import { Images } from "@/constants/images"
import { DefaultImage } from "@/lib/external-components"
import { ensureProtocol } from "@/utils/ensure-protocol-helper"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import Link from "next/link"
import "swiper/css"
import "swiper/css/pagination"
import { twMerge } from "tailwind-merge"
import { CommonStylesProps } from "../../../lib/commonCSSProps"
import { generateResponsiveCSS } from "../../../lib/helper"

export interface ContactProps extends CommonStylesProps {
  contactLabel: string
  contactInfo: {
    iconUrl: string
    linkContact: string
  }[]
  faqs: {
    question: string
    answer: string
  }[]
}

export const RenderConfig: ComponentConfig<ContactProps> = {
  render: ({ className, styles, responsiveType, contactInfo, faqs, contactLabel }) => {
    const id = `faqs-${Math.random().toString(36).substr(2, 9)}`
    const responsiveCSS = generateResponsiveCSS(id, styles || {}, responsiveType)

    return (
      <>
        {responsiveCSS}
        <div
          className={`${twMerge(className)} bg-[#F9F9FB] pt-8 pb-10 px-6 sm:p-8 lg:p-16 sm:rounded-2xl rounded-none`}
        >
          {/* Contact Expert Section */}
          <div className="mb-3 sm:mb-2">
            <h2 className="text-sm sm:text-base lg:text-lg font-semibold leading-5 sm:leading-6 lg:leading-[30px] text-[#0C9443] mb-2 sm:mb-3 lg:mb-4">
              {contactLabel}
            </h2>
            <div className="flex gap-2 sm:gap-3 lg:gap-4">
              {contactInfo.map((contact, index) => (
                <Link
                  key={index}
                  href={ensureProtocol(contact.linkContact) || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full flex items-center justify-center transition-transform hover:scale-105"
                >
                  <Image
                    src={(contact?.iconUrl && `${ADMIN_API_CONFIG.API_URL}${contact?.iconUrl}`) || DefaultImage}
                    alt="Contact Icon"
                    width={48}
                    height={48}
                    className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12"
                  />
                </Link>
              ))}
            </div>
          </div>

          {/* FAQ Accordion Section */}
          <Accordion type="multiple" defaultValue={["item-0"]} className="space-y-0">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border-b border-gray-200 px-2 sm:px-3 lg:px-6"
              >
                <AccordionTrigger className="w-full flex items-center justify-between py-3 sm:py-4 lg:py-6 text-left hover:bg-gray-50 transition-colors hover:no-underline [&>svg]:hidden [&[data-state=open]_.icon-down]:hidden [&[data-state=open]_.icon-up]:block [&[data-state=closed]_.icon-up]:hidden [&[data-state=closed]_.icon-down]:block">
                  <span className="text-sm sm:text-base lg:text-lg font-medium text-[#31343E] flex items-center gap-1 sm:gap-2 lg:gap-3 flex-1 pr-1 sm:pr-2">
                    <span className="text-[#31343E] font-semibold text-xs sm:text-sm lg:text-base shrink-0">
                      {index + 1}.
                    </span>
                    <span className="text-xs sm:text-sm lg:text-base leading-4 sm:leading-5 lg:leading-6 font-medium">
                      {faq.question}
                    </span>
                  </span>
                  <div className="relative w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 shrink-0">
                    <Image
                      src={Images.carretDown}
                      alt="Expand"
                      width={20}
                      height={20}
                      className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 icon-down"
                    />
                    <Image
                      src={Images.carretUp}
                      alt="Collapse"
                      width={20}
                      height={20}
                      className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 absolute top-0 left-0 icon-up"
                    />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-3 sm:pb-4 lg:pb-6 pr-0 sm:pr-6 lg:pr-12">
                  <div className="text-xs sm:text-sm lg:text-base text-[#31343E] leading-relaxed pl-4 sm:pl-5 lg:pl-6">
                    {(faq.answer || "").split("\n").map((paragraph, pIndex) => (
                      <p key={pIndex} className="mb-1 sm:mb-2 relative">
                        <span className="absolute -left-3 sm:-left-4 top-0 text-[#31343E] text-xs sm:text-sm font-normal">
                          •
                        </span>
                        <span>{paragraph}</span>
                      </p>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </>
    )
  },
}
